import React, { useMemo, Dispatch, useEffect } from 'react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import styles from './TimelineFilter.module.scss';
import { FilterIcon } from '@components/Icons';
import { selectContactTimelineAvailableFilters } from '@reducers/data/timeline';
import { ID } from '@app/types';
import { TimelineFilter } from '@models/Timeline';
import { fetchContactTimelineAvailableFilters } from '@actions/timeline';
import SelectMenu from '@components/Menus/SelectMenu';

interface TimelineFilterOption {
  label: string;
  value: TimelineFilter | null;
  disabled?: boolean;
}
export const defaultTimelineFilterOption = { label: 'All', value: undefined };
const allOptions: TimelineFilterOption[] = [
  { label: 'Notes', value: 'notes' },
  { label: 'Reminders', value: 'reminders' },
  { label: 'Emails', value: 'emails' },
  // { label: 'Messages', value: 'apple-message' },
  { label: 'Meetings', value: 'events' },
  { label: 'Calls', value: 'calls' },
];

export default function TimelineFilterSetter({
  contactID,
  timelineFilter,
  setTimelineFilter,
}: {
  contactID: ID;
  timelineFilter: TimelineFilter;
  setTimelineFilter: Dispatch<TimelineFilter>;
}) {
  const dispatch = useDispatch();
  const availableFilters = useSelector(selectContactTimelineAvailableFilters(contactID));

  const options = useMemo(() => {
    const availableOptions = allOptions.map((option) => {
      const available = availableFilters.includes(option.value);
      return { ...option, disabled: !available };
    });

    return [defaultTimelineFilterOption].concat(availableOptions);
  }, [availableFilters]);

  useEffect(() => {
    dispatch(fetchContactTimelineAvailableFilters({ contactID }));
  }, []);

  const valueLabel = useMemo(() => {
    if (!timelineFilter) {
      return 'Filter';
    }
    return options.find((option) => option.value === timelineFilter)?.label || 'Filter';
  }, [timelineFilter]);

  return (
    <SelectMenu
      options={options}
      value={timelineFilter}
      onChange={(value) => setTimelineFilter(value)}
      triggerClassName={cn(styles.filterTrigger, !!timelineFilter && styles.filterApplied)}
      menuClassName={styles.filterMenu}
      disabled={options.length === 1}
      triggerProps={{
        children: (
          <>
            <FilterIcon color="textPrimary" />
            <span className={styles.label}>{valueLabel}</span>
          </>
        ),
      }}
    />
  );
}
